import type React from "react";
import {useCallback, useState} from "react";
import {observer} from "mobx-react-lite";
import {generatePath, useNavigate, useParams} from "react-router";
import {Button, Skeleton} from "@mui/material";
import {routes} from "~/routing/routes";
import {useStores} from "~/stores";
import {useCollapsiblePanel} from "~/components/resizable-split/useCollapsiblePanel";
import {SupersetDashboardPanel} from "~/pages/chat-agent/components/SupersetDashboardPanel/SupersetDashboardPanel";
import {useChatContext} from "~/pages/chat-agent/context/ChatContext";
import type {EmbeddedDashboard} from "~/pages/chat-agent/types/chatTypes";
import type {DashboardSource} from "./services/dashboardsApi";
import {useHermesDashboard, useInvalidateMyDashboards, useMyDashboards} from "./services/dashboardsQueries";
import {AtlasDashboardPanel} from "./AtlasDashboardPanel";
import {DashboardDetailLayout} from "./DashboardDetailLayout";
import {DashboardsAtlasSplit} from "./DashboardsAtlasSplit";
import {DashboardsEmptyState} from "./DashboardsEmptyState";
import "./dashboards.scss";

export type DashboardDetailRouteMatch = {id: string; source: string; dashboardId: string};

function useDashboard(source: DashboardSource | null, dashboardId: string, bagId: number) {
    const hermes = useHermesDashboard(dashboardId, source === "hermes");
    const mine = useMyDashboards(source === "mine" ? bagId : 0);
    if (source === "hermes") {
        return {dashboard: hermes.data ?? null, isLoading: hermes.isPending, error: hermes.error};
    }
    if (source === "mine") {
        const dashboard = mine.data?.dashboards.find(d => d.id === dashboardId) ?? null;
        return {dashboard, isLoading: Boolean(bagId) && mine.isPending, error: mine.error};
    }
    return {dashboard: null, isLoading: false, error: null};
}

/**
 * A dashboard opened from the gallery, laid out like the chat route: the chat route's
 * SupersetDashboardPanel on the right and, for your own dashboards, Atlas docked on the left with
 * the conversation that built it. "← Dashboards / <name>" in the top bar, and Atlas's close button,
 * lead back to the gallery. Built-in dashboards fill the page on their own.
 */
export const DashboardDetailPage: React.FC = observer(() => {
    const navigate = useNavigate();
    const params = useParams<DashboardDetailRouteMatch>();
    const {bagStore} = useStores();
    const bagId = bagStore.bagId;
    const source: DashboardSource | null = params.source === "hermes" || params.source === "mine" ? params.source : null;
    const dashboardId = params.dashboardId ?? "";
    const {dashboard, isLoading, error} = useDashboard(source, dashboardId, bagId);
    const {embeddedDashboard, isRestoringSession} = useChatContext();

    const back = () => navigate(generatePath(routes.dashboards, {id: String(bagId)}));

    // Your dashboards open with Atlas docked. Full screen only collapses it, so its draft and
    // scroll survive. Every DashGen change reloads the embed.
    const atlasPanel = useCollapsiblePanel();
    const [embedVersion, setEmbedVersion] = useState(0);
    const invalidateMine = useInvalidateMyDashboards();
    const atlasSessionId = dashboard?.source === "mine" ? dashboard.sessionId : null;
    const atlasOpen = Boolean(atlasSessionId);
    const onDashboardChanged = useCallback(() => {
        setEmbedVersion(v => v + 1);
        void invalidateMine(bagId);
    }, [bagId, invalidateMine]);

    // Older agents list dashboards without a Superset domain; the chat Atlas restores carries it,
    // which is how the chat route shows the same dashboard.
    const fromChat = dashboard && embeddedDashboard
        && (embeddedDashboard.uuid === dashboard.embeddedUuid || embeddedDashboard.dashboard_id === dashboard.dashgenId)
        ? embeddedDashboard
        : null;
    const embed: EmbeddedDashboard | null = dashboard?.embeddedUuid && dashboard.supersetDomain
        ? {
            uuid: dashboard.embeddedUuid,
            superset_domain: dashboard.supersetDomain,
            dashboard_id: dashboard.dashgenId ?? 0,
            title: dashboard.title,
        }
        : fromChat && {...fromChat, title: dashboard?.title ?? fromChat.title};

    return (
        <DashboardsAtlasSplit
            atlas={atlasSessionId ? (
                <AtlasDashboardPanel
                    mode={{kind: "edit", sessionId: atlasSessionId}}
                    onClose={back}
                    onDashboardChanged={onDashboardChanged}
                />
            ) : null}
            atlasPanel={atlasOpen ? atlasPanel : undefined}
        >
            <DashboardDetailLayout onBack={back} title={dashboard?.title}>
                {isLoading || (!embed && atlasOpen && isRestoringSession) ? (
                    <Skeleton variant="rectangular" height="100%"/>
                ) : error ? (
                    <DashboardsEmptyState
                        variant="canvas"
                        role="alert"
                        title="This dashboard could not be loaded"
                        body={error.message}
                    />
                ) : !dashboard ? (
                    <DashboardsEmptyState
                        variant="canvas"
                        icon
                        title="Dashboard not found"
                        body="It may have been removed or unpublished."
                        action={<Button size="small" variant="outlined" onClick={back}>Back to dashboards</Button>}
                    />
                ) : !embed ? (
                    <DashboardsEmptyState
                        variant="canvas"
                        icon
                        title="This dashboard can't be shown here yet"
                        body={atlasOpen
                            ? "Atlas on the left can still view and edit it."
                            : "It has not been set up for embedding."}
                    />
                ) : (
                    <SupersetDashboardPanel
                        key={`${embed.uuid}@${embed.superset_domain}#${embedVersion}`}
                        dashboard={embed}
                        showTitle={false}
                        isFullscreen={!atlasOpen || atlasPanel.collapsed}
                        onToggleFullscreen={atlasOpen ? atlasPanel.toggle : undefined}
                    />
                )}
            </DashboardDetailLayout>
        </DashboardsAtlasSplit>
    );
});
